/**
 * ACT2-1108: the first-run setup panel's progress, shared by the chat home (the panel) and the sidebar
 * (its "Setup N of 5" entry).
 *
 * Progress is workspace-wide and comes from `setup.status`. Two things are per person and live in this
 * browser's localStorage: "Hide" and "Looks good" on the optional settings step. They are kept in one
 * store that every caller subscribes to, so the sidebar entry and the panel agree without a reload.
 */
import { useQuery } from '@tanstack/react-query';
import { useCallback, useSyncExternalStore } from 'react';

import { useWorkspace } from '@/_shared/contexts/WorkspaceContext';
import { useAuth } from '@/_shared/hooks/useAuth';
import { useOrpc } from '@/_shared/hooks/useOrpc';
import {
  firstOpenSetupStep,
  setupDoneCount,
  type SetupStepId,
  setupStepsDone,
  type WorkspaceSetupStatus,
} from '@/_shared/lib/setup-steps';

export interface SetupPrefs {
  /** This person hid the panel. The sidebar entry brings it back. */
  hidden: boolean;
  /** This person said the workspace settings look good. */
  settingsReviewed: boolean;
}

const DEFAULT_PREFS: SetupPrefs = { hidden: false, settingsReviewed: false };

const listeners = new Set<() => void>();
// One parsed snapshot per key, so `useSyncExternalStore` sees a stable object between writes.
const snapshots = new Map<string, SetupPrefs>();

function readPrefs(key: string): SetupPrefs {
  const cached = snapshots.get(key);
  if (cached) return cached;
  let prefs = DEFAULT_PREFS;
  try {
    const raw = window.localStorage.getItem(key);
    if (raw !== null) {
      const parsed = JSON.parse(raw) as Partial<SetupPrefs> | null;
      prefs = {
        hidden: parsed?.hidden === true,
        settingsReviewed: parsed?.settingsReviewed === true,
      };
    }
  } catch {
    // Storage blocked or the value is not ours: start from the defaults.
  }
  snapshots.set(key, prefs);
  return prefs;
}

function writePrefs(key: string, next: SetupPrefs): void {
  snapshots.set(key, next);
  try {
    window.localStorage.setItem(key, JSON.stringify(next));
  } catch {
    // Storage blocked or full: the value still holds until the page reloads.
  }
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  // Another tab changed it: drop the cached snapshot so the next read picks the new value up.
  const onStorage = (event: StorageEvent): void => {
    if (event.key !== null && snapshots.has(event.key)) snapshots.delete(event.key);
    listener();
  };
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', onStorage);
  };
}

/** Test seam: forget cached snapshots so a test's localStorage setup is read afresh. */
export function resetSetupPrefsCache(): void {
  snapshots.clear();
}

function useSetupPrefs(): SetupPrefs & {
  setHidden: (hidden: boolean) => void;
  setSettingsReviewed: (reviewed: boolean) => void;
} {
  const { slug } = useWorkspace();
  const { user } = useAuth();
  const key = `lookout.setup.${slug}.${user?.id ?? 'anonymous'}`;
  const prefs = useSyncExternalStore(subscribe, () => readPrefs(key));
  const setHidden = useCallback((hidden: boolean) => writePrefs(key, { ...readPrefs(key), hidden }), [key]);
  const setSettingsReviewed = useCallback(
    (settingsReviewed: boolean) => writePrefs(key, { ...readPrefs(key), settingsReviewed }),
    [key],
  );
  return { ...prefs, setHidden, setSettingsReviewed };
}

export interface SetupProgress {
  /** Null until `setup.status` loads, and always for a member or with the setup panel flag off. */
  status: WorkspaceSetupStatus | null;
  done: Record<SetupStepId, boolean> | null;
  doneCount: number;
  /** The first step still to do, or null. */
  currentStep: SetupStepId | null;
  /** Every counted step is done: the panel and the sidebar entry go away. */
  complete: boolean;
  prefs: ReturnType<typeof useSetupPrefs>;
}

/** How often the chat home re-reads the status while setup is incomplete. */
const SETUP_STATUS_POLL_MS = 15_000;

/**
 * `poll`: re-read the status every so often while the panel shows (setup incomplete and not hidden), so a
 * step done in another tab or by another admin ticks here too. Only the chat home, where the panel is,
 * passes it; the sidebar entry reads the same cache.
 */
export function useSetupProgress({ poll = false }: { poll?: boolean } = {}): SetupProgress {
  const { isAdmin, isSetupPanelEnabled } = useWorkspace();
  // ACT2-1108: only where the setup panel is on (`enable-lookout-setup-panel`); off, there is no setup.
  const active = isAdmin && isSetupPanelEnabled;
  const orpc = useOrpc();
  const prefs = useSetupPrefs();
  const { data } = useQuery({
    ...orpc.setup.status.queryOptions(),
    enabled: active,
    // Only while the panel shows: not after this person hid it, and never once setup is complete.
    refetchInterval: (query) =>
      poll && !prefs.hidden && query.state.data?.status.complete === false ? SETUP_STATUS_POLL_MS : false,
  });
  const status = active ? (data?.status ?? null) : null;
  const done = status ? setupStepsDone(status, { settingsReviewed: prefs.settingsReviewed }) : null;
  return {
    status,
    done,
    doneCount: done ? setupDoneCount(done) : 0,
    currentStep: done ? firstOpenSetupStep(done) : null,
    complete: status?.complete ?? false,
    prefs,
  };
}

/**
 * Whether the chat home is the setup page right now: an admin's setup is incomplete and the panel is not
 * hidden. The home's title and the sidebar's active item then read "Setup" instead of "New chat".
 */
export function isSetupOnHome(progress: SetupProgress): boolean {
  return progress.status !== null && !progress.complete && !progress.prefs.hidden;
}
